import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { formatMoney } from "@bw/shared/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  getFacilityDashboard,
  type FacilityDashboardStatsResponse,
} from "@/libs/analyticsApi";
import { listDailyBookings, type Booking } from "@/libs/bookingsApi";
import { listCourts, type Court } from "@/libs/facilityApi";
import {
  createZonedTimeFormatter,
  toZonedCivilDate,
} from "@/libs/zonedDateTime";

const QUICK_ACTIONS = [
  { to: "/front-desk", label: "Front desk" },
  { to: "/bookings", label: "Reservations" },
  { to: "/services/drop-in?tab=dashboard", label: "Drop-in" },
  { to: "/check-in", label: "Check-in" },
  { to: "/calendar", label: "Calendar" },
] as const;

const HIDDEN_BOOKING_STATUSES = new Set(["cancelled"]);
const PLACEHOLDER = "—";
const STATS_ERROR =
  "Today’s facility stats are unavailable. Try again shortly.";
const BOOKINGS_ERROR = "Today’s bookings are unavailable. Try again shortly.";
const COURT_NAMES_ERROR =
  "Court names are unavailable. Booking rows use a placeholder.";

const STATUS_COLOR = {
  confirmed: "primary",
  completed: "success",
  cancelled: "error",
  no_show: "error",
} as const;

interface DashboardSnapshot {
  facilityId: string;
  stats: FacilityDashboardStatsResponse | null;
  bookings: Booking[];
  courts: Court[];
  statsError: string | null;
  glanceError: string | null;
  courtNamesError: string | null;
}

function utilizationPct(inUse: number, total: number): number {
  if (!total || total <= 0) return 0;
  const percentage = Math.round((inUse / total) * 100);
  return Math.min(100, Math.max(0, percentage));
}

function bookingStatusLabel(status: string): string {
  return status.replaceAll("_", " ");
}

function bookingIdentity(booking: Booking): string {
  return (
    booking.display_name?.trim() || booking.event_name?.trim() || "Reserved"
  );
}

export function OperatorDashboard() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const facilityTimeZone = facility?.timezone || "America/Los_Angeles";
  const facilityTimeFormatter = useMemo(
    () => createZonedTimeFormatter(facilityTimeZone),
    [facilityTimeZone],
  );
  const orgView = useMyOrg();
  const orgIsApproved =
    orgView.kind === "onboarding" && orgView.status === "approved";

  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const requestGeneration = useRef(0);

  useEffect(() => {
    if (!facilityId) return;

    let cancelled = false;
    const generation = ++requestGeneration.current;
    const targetFacilityId = facilityId;
    const today = toZonedCivilDate(new Date(), facilityTimeZone);
    setLoading(true);

    void (async () => {
      const [statsResult, bookingsResult, courtsResult] =
        await Promise.allSettled([
          getFacilityDashboard(targetFacilityId),
          listDailyBookings(targetFacilityId, today),
          listCourts(targetFacilityId),
        ]);

      if (cancelled || generation !== requestGeneration.current) return;

      setSnapshot({
        facilityId: targetFacilityId,
        stats: statsResult.status === "fulfilled" ? statsResult.value : null,
        bookings:
          bookingsResult.status === "fulfilled" ? bookingsResult.value : [],
        courts: courtsResult.status === "fulfilled" ? courtsResult.value : [],
        statsError: statsResult.status === "rejected" ? STATS_ERROR : null,
        glanceError:
          bookingsResult.status === "rejected" ? BOOKINGS_ERROR : null,
        courtNamesError:
          courtsResult.status === "rejected" ? COURT_NAMES_ERROR : null,
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [facilityId, facilityTimeZone]);

  const activeSnapshot = snapshot?.facilityId === facilityId ? snapshot : null;
  const currentLoading = loading || activeSnapshot === null;
  const stats = activeSnapshot?.stats ?? null;
  const bookings = activeSnapshot?.bookings ?? [];
  const courts = activeSnapshot?.courts ?? [];
  const statsError = currentLoading ? null : activeSnapshot?.statsError;
  const glanceError = currentLoading ? null : activeSnapshot?.glanceError;
  const courtNamesError = currentLoading
    ? null
    : activeSnapshot?.courtNamesError;

  const courtNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const court of courts) names.set(court.court_id, court.name);
    return names;
  }, [courts]);

  const glanceRows = useMemo(
    () =>
      bookings
        .filter((booking) => !HIDDEN_BOOKING_STATUSES.has(booking.status))
        .slice()
        .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    [bookings],
  );

  const isFirstDay =
    !currentLoading &&
    stats !== null &&
    Number(stats.today_revenue) === 0 &&
    stats.today_sessions === 0 &&
    stats.today_checkins === 0;

  if (facilityLoading || !facilityId) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading facility overview…
        </Typography>
      </Box>
    );
  }

  const hasStats = stats !== null;
  const hasNoCourts = hasStats && stats.courts_total === 0;
  const occupancy = hasStats
    ? `${stats.courts_in_use}/${stats.courts_total}`
    : PLACEHOLDER;
  const utilization = hasStats
    ? `${utilizationPct(stats.courts_in_use, stats.courts_total)}% utilization`
    : undefined;
  const valueFromStats = (
    render: (value: FacilityDashboardStatsResponse) => number | string,
  ) => {
    if (currentLoading || !stats) return PLACEHOLDER;
    return render(stats);
  };
  const summary = [
    {
      label: "Revenue Today",
      value: valueFromStats((value) =>
        value.currency ? formatMoney(value.today_revenue, value.currency) : "—",
      ),
    },
    {
      label: "Court occupancy",
      value: currentLoading || !hasStats ? PLACEHOLDER : occupancy,
      detail: currentLoading ? undefined : utilization,
    },
    {
      label: "Check-ins today",
      value: valueFromStats((value) => value.today_checkins),
    },
    {
      label: "In Queue",
      value: valueFromStats((value) => value.queue_size),
    },
    {
      label: "Active Members",
      value: valueFromStats((value) => value.active_members),
    },
  ];

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Facility overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {facility?.name ?? "Your facility"}
        </Typography>
      </Box>

      {statsError ? <Alert severity="error">{statsError}</Alert> : null}

      <Paper
        component="section"
        aria-label="Today’s facility summary"
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 } }}
      >
        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {summary.map((item) => (
            <Box key={item.label} sx={{ minWidth: 0 }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {item.label}
              </Typography>
              <Typography
                component="dd"
                variant="h6"
                sx={{
                  m: 0,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {item.value}
              </Typography>
              {item.detail ? (
                <Typography variant="caption" color="text.secondary">
                  {" "}
                  {item.detail}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </Paper>

      <Stack
        component="nav"
        aria-label="Facility shortcuts"
        direction="row"
        useFlexGap
        spacing={0.75}
        sx={{ flexWrap: "wrap" }}
      >
        {QUICK_ACTIONS.map(({ to, label }) => (
          <Button
            key={to}
            component={Link}
            to={to}
            size="small"
            variant="text"
            sx={{ minHeight: 44 }}
          >
            {label}
          </Button>
        ))}
      </Stack>

      {orgIsApproved ? (
        <Card
          component="section"
          aria-labelledby="approved-launch-heading"
          variant="outlined"
        >
          <CardContent>
            <Stack spacing={1.25} sx={{ alignItems: "flex-start" }}>
              <Typography
                id="approved-launch-heading"
                component="h2"
                variant="h6"
              >
                Organization approved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your business review is complete. Each facility still needs its
                own setup, service readiness, and facility review before players
                can find or book it.
              </Typography>
              <Button
                component={Link}
                to="/getting-started"
                size="small"
                variant="contained"
                sx={{ minHeight: 44 }}
              >
                Review facility launch
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : isFirstDay && hasNoCourts ? (
        <Card
          component="section"
          aria-labelledby="launch-checklist-heading"
          variant="outlined"
        >
          <CardContent>
            <Stack spacing={1.25} sx={{ alignItems: "flex-start" }}>
              <Typography
                id="launch-checklist-heading"
                component="h2"
                variant="h6"
              >
                Launch checklist
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your facility hasn&apos;t taken any sessions yet today. Set up
                your courts so players can start booking.
              </Typography>
              <Button
                component={Link}
                to="/courts"
                size="small"
                variant="contained"
                sx={{ minHeight: 44 }}
              >
                Set up courts
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card
        component="section"
        role="region"
        aria-labelledby="today-at-a-glance-heading"
        variant="outlined"
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 1.5,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              id="today-at-a-glance-heading"
              component="h2"
              variant="h6"
            >
              Today at a Glance
            </Typography>
            <Button
              component={Link}
              to="/calendar"
              size="small"
              variant="text"
              sx={{ minHeight: 44, flexShrink: 0 }}
            >
              View calendar
            </Button>
          </Stack>

          <Stack spacing={1.25}>
            {glanceError ? <Alert severity="error">{glanceError}</Alert> : null}
            {courtNamesError ? (
              <Alert severity="warning">{courtNamesError}</Alert>
            ) : null}

            <TableContainer sx={{ overflowX: { xs: "visible", sm: "auto" } }}>
              <Table
                size="small"
                aria-label="Today’s bookings"
                sx={{
                  minWidth: { xs: 0, sm: 680 },
                  tableLayout: { xs: "fixed", sm: "auto" },
                  "& th, & td": { px: { xs: 0.75, sm: 2 } },
                  "& th:first-of-type, & td:first-of-type": {
                    width: { xs: "34%", sm: "auto" },
                  },
                  "& th:nth-of-type(2), & td:nth-of-type(2)": {
                    width: { xs: "18%", sm: "auto" },
                  },
                  "& th:nth-of-type(3), & td:nth-of-type(3)": {
                    width: { xs: "26%", sm: "auto" },
                  },
                  "& th:nth-of-type(4), & td:nth-of-type(4)": {
                    display: { xs: "none", sm: "table-cell" },
                  },
                  "& th:nth-of-type(5), & td:nth-of-type(5)": {
                    width: { xs: "22%", sm: "auto" },
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Player / event</TableCell>
                    <TableCell>Court</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentLoading ? (
                    Array.from({ length: 3 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={5}>
                          <Skeleton variant="text" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : glanceRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ py: 4, color: "text.secondary" }}
                      >
                        {glanceError ? PLACEHOLDER : "No bookings yet today."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    glanceRows.map((booking) => (
                      <TableRow key={booking.booking_id}>
                        <TableCell
                          sx={{ fontWeight: 600, overflowWrap: "anywhere" }}
                        >
                          {bookingIdentity(booking)}
                        </TableCell>
                        <TableCell sx={{ overflowWrap: "anywhere" }}>
                          {courtNames.get(booking.court_id) ?? PLACEHOLDER}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {facilityTimeFormatter.format(
                            new Date(booking.start_time),
                          )}
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {formatMoney(
                            booking.amount_charged,
                            booking.currency,
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={
                              STATUS_COLOR[
                                booking.status as keyof typeof STATUS_COLOR
                              ] ?? "default"
                            }
                            label={bookingStatusLabel(booking.status)}
                            sx={{
                              maxWidth: "100%",
                              textTransform: "capitalize",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
